/**
 * The people who work a station.
 *
 * A berth used to be five labelled panels. The trade screen was a price list,
 * the mission board was a notice that there were no missions, and none of it
 * suggested anyone was there — which is a strange thing for the only place in
 * the game with air in it.
 *
 * So each service has somebody behind it, and they say something when you walk
 * in. This is cheap to build and does a great deal: a name attached to a price
 * list turns "the trader" from a menu into a person you are buying from, and
 * the same names recurring makes a station somewhere you have been before.
 *
 * ── Names are fixed, lines are not ──────────────────────────────────────────
 *
 * The staff are seeded off the station, so the mechanic at Tessaract Reach is
 * the same mechanic every time you dock there — that is the whole point, and a
 * crew that reshuffled between visits would be worse than anonymity. What they
 * *say* is picked fresh on each arrival, because a person who greets you with
 * the identical sentence forever stops reading as a person at all.
 *
 * ── Control is not a new person ─────────────────────────────────────────────
 *
 * `control` deliberately draws from the same entity stream the flight view uses
 * for the voice that talks you in, so the officer watching the reputation board
 * *is* the one who cleared your approach. Two independently generated people
 * sharing one post would be a small lie the player could catch.
 */
import { Rng } from './rng.js';
import { makeOfficial, makePersonName } from './names.js';
/** The post each role holds, as it is written on the panel. */
export const STAFF_TITLES = {
    manager: 'Station Manager',
    trader: 'Trader',
    control: 'Spacefare Control',
    mechanic: 'Mechanic',
    painter: 'Painter',
    fabricator: 'Fabricator',
};
/**
 * What each of them says.
 *
 * Written in voice rather than as help text. The panel below already explains
 * the mechanics; this is the person, and a person who talks like a tooltip is
 * worse than no person. Each is a complete greeting on its own — they are shown
 * one at a time and never in sequence, so none of them may depend on another
 * having been read.
 */
const LINES = {
    manager: [
        'Plenty to be done round here and never enough hulls to do it. Take a job off my hands, would you?',
        'If you have got space in that hold, I have got somewhere it needs to be.',
        'Half this board has been up a fortnight. Run an errand or two and I will think kindly of you.',
        'Freight does not move itself. Well — some of it does, and that is a different problem.',
        'You look like someone with a schedule and no idea what to put in it.',
    ],
    trader: [
        'For linelits I can give you many great things. Come, look.',
        'Everything here is worth what I say it is worth. Fortunately I am reasonable.',
        'Buying, selling, both — I am not fussy which way the crates go.',
        'Good line, fair weight, honest price. Two of those three, anyway.',
        'Everything for a price, but it\'s a good price, I assure you.',
    ],
    control: [
        'Us guilds track everyone in this neck of the woods. Nothing personal — it is just the job.',
        'Your standing follows you out there. Worth remembering before you do something clever.',
        'Every hull that passes through goes on the record. Yours included.',
        'People think the board is about who likes them. It is about who answers when you call.',
        'I have got names on this list who would be very surprised to see where they sit.',
    ],
    mechanic: [
        'Want to make modifications? I am your guy.',
        'Bring it in and I will open it up. No hull is finished, whatever the builder told you.',
        'Bent line, cracked joint, something rattling you cannot place — I have seen worse this week.',
        'You fly it, I fix it. Try to keep those in that order.',
        'There is nothing on that ship I cannot take off and put back better.',
    ],
    painter: [
        'Looking to change things up?',
        'Same hull, new colours. Cheapest way there is to feel like a different pilot.',
        'Everyone out there is grey. You do not have to be.',
        'I can make it handsome. I cannot make it fast — that is next door.',
        'Tell me what you want it to look like and I will tell you what it will cost.',
    ],
    fabricator: [
        "Putting these linelits back into lines ain't that hard, but none of these fools can figure it out.",
        'Melt it down, draw it out, cut it to length. People act like it is witchcraft.',
        'Half the yards in the sector send their resizing here. Says everything, really.',
        'You want a length that does not exist yet? Sit down, this is the easy part.',
        'Linelits in, line out. I have been doing it longer than most of them have been flying.',
    ],
};
/**
 * Who holds this post at this station.
 *
 * Seeded on the station and the role, so the crew is stable across visits and
 * two stations never share a mechanic.
 */
export function crewmember(seed, stationId, role) {
    const rng = Rng.forEntity(seed, stationId, role);
    const title = STAFF_TITLES[role];
    // Control speaks for an organisation and carries a rank with it; the rest are
    // tradespeople, and "Warden Kelis Marchetti, Trader" would be nonsense.
    if (role === 'control') {
        const official = makeOfficial(rng, title);
        return { role, title, name: official.name, signature: official.signature };
    }
    const name = makePersonName(rng);
    return { role, title, name, signature: `${title} ${name}` };
}
/**
 * Something for them to say.
 *
 * The caller supplies the randomness, because the choice is per *visit* rather
 * than per station — seeding it off the station would pin each of them to one
 * line forever, which is the thing this is meant to avoid.
 */
export function crewLine(role, random) {
    const lines = LINES[role];
    const at = Math.min(lines.length - 1, Math.floor(random.float() * lines.length));
    return lines[at];
}
/** Every line a role has, for tests and for anything that wants to audit them. */
export function crewLines(role) {
    return LINES[role];
}
//# sourceMappingURL=staff.js.map